import React, { FC, useState } from "react";
import styles from "./FormModalWindow.module.scss";
import { PUBLIC_IMAGE } from "../../constants";
import MyButton from "../UI/MyButton/MyButton";
import formStore from "../../stores/formStore";
import { observer } from "mobx-react-lite";
import SliderCaptcha from "./SliderCaptcha/SliderCaptcha";

const FormModalWindow: FC = observer(() => {
  const qrImage = PUBLIC_IMAGE + "modal-qr.png";
  const [captchaDone, setCaptchaDone] = useState(false);
  const [addressCopied, setAddressCopied] = useState(false);
  const [copiedField, setCopiedField] = useState("");
  const response = formStore.finalData?.response || formStore.finalData || {};
  const isProviderOrder = Boolean(
    formStore.formData.exchangeOrderId || response.provider || response.order_id
  );
  const providerId =
    formStore.formData.exchangeProvider ||
    response.provider ||
    response.provider_id ||
    response.providerId ||
    "";
  const providerName =
    String(providerId).toLowerCase() === "simpleswap"
      ? "SimpleSwap"
      : providerId || "Exchange provider";
  const walletAddress: string =
    formStore.formData.exchangeDepositAddress ||
    response.deposit_address ||
    (formStore.formData.pay === "Tether TRC20 (USDT)" ||
    formStore.formData.pay === "Tether ERC20 (USDT)" ||
    formStore.formData.pay === "Ethereum (ETH)"
      ? "alfa-crypto.eth"
      : "");
  const formatAmount = (value: unknown) => {
    if (value === null || value === undefined || value === "") return "";
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return String(value);
    return numeric.toLocaleString("en-US", {
      maximumFractionDigits: 8,
      useGrouping: false,
    });
  };
  const sentAmount = formatAmount(
    response.deposit_amount ||
    response.send_amount ||
    response.amount_from ||
      formStore.formData.paySelect
  );
  const sentAsset =
    response.deposit_asset && response.deposit_network
      ? `${response.deposit_asset} ${response.deposit_network}`
      : formStore.formData.pay;
  const sentDisplay = `${sentAmount} ${sentAsset}`.trim();
  const receiveAmount = formatAmount(
    response.receive_amount ||
    response.expected_receive_amount ||
    response.amount_to ||
      formStore.formData.receiveSelect
  );
  const receiveAsset =
    response.receive_asset && response.receive_network
      ? `${response.receive_asset} ${response.receive_network}`
      : formStore.formData.receive;
  const orderStatus =
    formStore.formData.exchangeOrderStatus || response.status || "waiting";
  const createdAt = String(formStore.finalData.datetime || "").replace(",", "");

  const handleCaptchaSuccess = () => {
    setCaptchaDone(true);
  };

  const handleCaptchaFailure = () => {
    setCaptchaDone(false);
  };

  const scrollToTop = () => {
    const offset = (window.innerHeight * 30) / 100;
    window.scrollTo({ top: offset, behavior: "smooth" });
  };

  const handleButtonPaid = async () => {
    if (captchaDone || isProviderOrder) {
      formStore.setIsPaid(1);
      scrollToTop();
      await formStore.updateField("isPayd", "yes");
      try {
        const res = await fetch(
          "/api/v1/exchange/newpayd",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(formStore.formData),
          }
        );
        if (res.ok) {
          formStore.setIsPaid(1);
        } else {
          Error(`Ошибка передачи post payd: ${res.status}`);
        }
      } catch {}
    } else {
      alert("The Captcha has not been solved");
    }
  };

  const handleButtonClose = () => {
    formStore.setDataValid(undefined);
  };

  const copyText = async (value: string, field: string) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.setAttribute("readonly", "true");
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }

    setCopiedField(field);
    setTimeout(() => setCopiedField(""), 1500);
  };

  const handleCopyAddress = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    copyText(walletAddress, "address");
  };

  const handleButtonCancel = () => {
    formStore.setIsPaid(2);
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className={styles.modal__window}>
      <img
        className={styles.modal__widowClose}
        src={PUBLIC_IMAGE + "closeForm.svg"}
        onClick={handleButtonClose}
      ></img>
      <div className={styles.modal__windowWrapper}>
        <div className={styles.modal__howToPlay}></div>
        <div className={styles.modal__canceled}></div>
        {isProviderOrder ? (
          <div className={styles.provider__intro}>
            <div className={styles.provider__eyebrow}>Exchange order</div>
            <div className={styles.provider__title}>Payment details</div>
            <div className={styles.provider__text}>
              Send only the exact amount to the deposit address issued by the
              provider for this order.
            </div>
            <div className={styles.provider__badge}>{providerName}</div>
          </div>
        ) : (
          <div className={styles.modal__QrWrapper}>
            <div className={styles.qr__imageWrapper}>
              <img src={qrImage} className={styles.qr__image}></img>
            </div>
            <div className={styles.qr__text}>
              Please note that the rate is fixed after 12 network confirmations.
              Attention! Your transaction will be checked according to the AML
              Policy of the exchange office.
            </div>
          </div>
        )}
        {isProviderOrder ? (
          <div className={styles.provider__addressBlock}>
            <div className={styles.provider__addressHeader}>
              <div className={styles.provider__addressLabel}>
                Provider deposit address
              </div>
              <button
                className={styles.provider__copyButton}
                type="button"
                onClick={handleCopyAddress}
              >
                {copiedField === "address" || addressCopied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className={styles.provider__addressValue}>{walletAddress}</div>
          </div>
        ) : (
          <div className={styles.modal__sliderWrapper}>
            <SliderCaptcha
              onSuccess={handleCaptchaSuccess}
              onFailure={handleCaptchaFailure}
              walletAddress={walletAddress}
            />
            <div className={styles.slider__walletAddress}>{walletAddress}</div>
          </div>
        )}
        <div className={styles.modal__afterPayment}>
          {isProviderOrder
            ? "After sending the funds, click I PAID. The provider will continue the exchange after deposit confirmation."
            : 'After payment. Click on the "I paid the application" button.'}
        </div>
        {isProviderOrder ? (
          <div className={styles.provider__summary}>
            <div className={styles.provider__amountCards}>
              <div className={styles.provider__amountCard}>
                <div className={styles.provider__amountHeader}>
                  <div className={styles.provider__summaryLabel}>You send</div>
                  <button
                    className={styles.provider__inlineCopy}
                    type="button"
                    aria-label="Copy sent amount"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      copyText(sentAmount, "sent");
                    }}
                  >
                    {copiedField === "sent" ? "Copied" : "Copy"}
                  </button>
                </div>
                <div className={styles.provider__amountValue}>
                  {sentDisplay}
                </div>
              </div>
              <div className={styles.provider__amountCard}>
                <div className={styles.provider__amountHeader}>
                  <div className={styles.provider__summaryLabel}>You receive</div>
                </div>
                <div className={styles.provider__amountValue}>
                  {receiveAmount} {receiveAsset}
                </div>
              </div>
            </div>
            <div className={styles.provider__details}>
              <div className={styles.provider__detailRow}>
                <div className={styles.provider__detailLabel}>Provider</div>
                <div className={styles.provider__detailValue}>
                  {providerName}
                </div>
              </div>
              <div className={styles.provider__detailRow}>
                <div className={styles.provider__detailLabel}>Order ID</div>
                <div
                  className={`${styles.provider__detailValue} ${styles.provider__summaryHash}`}
                >
                  {formStore.formData.transactionId || "Pending"}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.modal__amount}>
            <div className={styles.amount__text}>
              <div className={styles.amount__textWeaving}>Sent</div>
              <div className={styles.amount__textReceived}>Received</div>
              <div className={styles.amount__textReceipt}>Transaction ID</div>
            </div>
            <div className={styles.amount__value}>
              <div className={styles.amount__valueWeaving}>
                {sentAmount} {sentAsset}
              </div>
              <div className={styles.amount__valueReceived}>
                {receiveAmount} {receiveAsset}
              </div>
              <div className={styles.amount__valueReceipt}>
                {formStore.formData.transactionId || "Pending"}
              </div>
            </div>
          </div>
        )}
        <div className={styles.modal__beCareful}>
          Please be careful! All fields must be filled in exact accordance with
          the instructions. Otherwise, the payment may not go through.
        </div>
      </div>
      <div className={styles.modal__buttonGroup}>
        {isProviderOrder && (
          <button
            className={styles.modal__buttonBack}
            type="button"
            onClick={handleButtonClose}
          >
            Change order
          </button>
        )}
        <MyButton
          className={styles.modal__buttonPaid}
          onClick={handleButtonPaid}
        >
          I paid{" "}
        </MyButton>
      </div>
      <div className={styles.modal__timeStatus}>
        <div className={styles.timestatus__wrapper}>
          <div className={styles.timestatus__timeText}>Created</div>
          <div className={styles.timestatus__dateTime}>
            {createdAt}
          </div>
        </div>
        <div className={styles.timestatus__line}></div>
        <div className={styles.timestatus__wrapper}>
          <div className={styles.timestatus__statusText}>
            Status
          </div>
          <div className={styles.timestatus__dateTime}>
            {isProviderOrder ? orderStatus : formStore.finalData.datetime}
          </div>
        </div>
      </div>
      {/* <div className={styles.modal__attention}>
        Attention! Click on the "Refresh page" button if you want to activate
        the automatic page update. The page will be updated every 30 seconds
      </div>
      <button className={styles.modal__buttonRefresh} onClick={handleReload}>
        Refresh page
      </button> */}
      <div className={styles.modal__bottomLine}></div>
    </div>
  );
});

export default FormModalWindow;
